/**
 * AI Debug Inspector — developer-only tool for inspecting AI chat context.
 *
 * Shows detailed breakdown of system prompts (by category), MCP tool definitions
 * with token estimates, and aggregate totals. Only rendered in dev mode.
 *
 * Renders as a grey chip in ToolModulesIndicator that opens a tabbed dialog.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { Bug, ChevronDown, ChevronRight, ChevronUp, Code, Eye, FileText, Kanban, Loader2, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { DialogForm, ResponsiveDialog } from '../common';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { chatApi, type SystemPromptSection, type SystemPromptSectionChild, type SystemPromptDebugInfo, type DebugToolDetail, type ContextTelemetryRound } from '../../../modules/ai_chat/services/chatApi';
import { MarkdownDisplay } from '../common/MarkdownDisplay';
import { useAuth } from '../../auth';

// Dev-only component — all literal strings are intentional (not user-facing)
/* eslint-disable i18next/no-literal-string */

interface SystemPromptViewerProps {
  sessionId: string;
}

/** Token count badge */
const TokenBadge: React.FC<{ tokens: number; className?: string }> = ({ tokens, className }) => (
  <span className={cn('text-[10px] font-mono text-muted-foreground/70 tabular-nums', className)}>
    ~{tokens.toLocaleString()} tok
  </span>
);

/** Inner component that uses hooks — always rendered when parent decides to show it */
const SystemPromptViewerInner: React.FC<SystemPromptViewerProps> = ({ sessionId }) => {
  const { t } = useTranslation('aiChat');
  const [open, setOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState<SystemPromptDebugInfo | null>(null);
  const [sections, setSections] = useState<SystemPromptSection[]>([]);
  const [contextTelemetry, setContextTelemetry] = useState<ContextTelemetryRound[] | null>(null);
  const [contextWindowTokens, setContextWindowTokens] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const response = await chatApi.getSystemPrompt(sessionId);
      setSections(response.data.sections);
      setDebugInfo(response.data.debug);
      setContextTelemetry(response.data.context_telemetry ?? null);
      setContextWindowTokens(response.data.context_window_tokens ?? null);
    } catch {
      setError(t('systemPrompt.loadError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleOpen}
            className={cn(
              'inline-flex items-center justify-center rounded-md h-7 w-7 transition-colors',
              'text-muted-foreground/60 hover:bg-muted/30 cursor-pointer',
            )}
          >
            <Bug className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">
          {t('debugInspector.tooltip')}
        </TooltipContent>
      </Tooltip>

      <DialogForm
        open={open}
        onClose={() => setOpen(false)}
        title={t('debugInspector.title')}
        hideActions
        maxWidth="lg"
      >
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive py-4">{error}</p>
        )}

        {!loading && !error && (
          <DebugContent
            sections={sections}
            debugInfo={debugInfo}
            contextTelemetry={contextTelemetry}
            contextWindowTokens={contextWindowTokens}
          />
        )}
      </DialogForm>
    </>
  );
};

/** Wrapper that gates on dev mode or sysadmin tenant */
export const SystemPromptViewer: React.FC<SystemPromptViewerProps> = (props) => {
  const { user } = useAuth();
  if (!import.meta.env.DEV && !user?.is_sysadmin) return null;
  return <SystemPromptViewerInner {...props} />;
};

// ── Category helpers ───────────────────────────────────────────────────

const CATEGORY_ORDER = ['general', 'platform', 'tenant', 'user', 'module', 'agent_context', 'memory', 'tools'] as const;

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  platform: 'Platform',
  tenant: 'Tenant',
  user: 'User',
  module: 'Module',
  agent_context: 'AI Agent Context',
  memory: 'Memory',
  tools: 'Tools',
};

function groupSectionsByCategory(sections: SystemPromptSection[]) {
  const groups: Record<string, SystemPromptSection[]> = {};
  for (const section of sections) {
    const cat = section.category || 'general';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(section);
  }
  return groups;
}

function groupToolsByModule(tools: DebugToolDetail[]) {
  const groups: Record<string, DebugToolDetail[]> = {};
  for (const tool of tools) {
    if (!groups[tool.module]) groups[tool.module] = [];
    groups[tool.module].push(tool);
  }
  return groups;
}

// ── Debug content with tabs ────────────────────────────────────────────

const DebugContent: React.FC<{
  sections: SystemPromptSection[];
  debugInfo: SystemPromptDebugInfo | null;
  contextTelemetry: ContextTelemetryRound[] | null;
  contextWindowTokens: number | null;
}> = ({ sections, debugInfo, contextTelemetry, contextWindowTokens }) => {
  if (!debugInfo && sections.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No debug data available.</p>;
  }

  const enrichedSections = debugInfo?.sections || sections;
  const totals = debugInfo?.totals;
  const hasContext = contextTelemetry && contextTelemetry.length > 0;

  return (
    <Tabs defaultValue="prompts" className="w-full">
      <TabsList className="w-full justify-start">
        <TabsTrigger value="prompts">Prompts</TabsTrigger>
        <TabsTrigger value="tools">
          Tools {totals ? `(${totals.tool_count})` : ''}
        </TabsTrigger>
        <TabsTrigger value="summary">Summary</TabsTrigger>
        <TabsTrigger value="context">
          Context {hasContext ? `(${contextTelemetry.length})` : ''}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="prompts" className="mt-3">
        <PromptsTab sections={enrichedSections} />
      </TabsContent>

      <TabsContent value="tools" className="mt-3">
        <ToolsTab tools={debugInfo?.tools || []} totals={totals} />
      </TabsContent>

      <TabsContent value="summary" className="mt-3">
        <SummaryTab debugInfo={debugInfo} sections={enrichedSections} />
      </TabsContent>

      <TabsContent value="context" className="mt-3">
        <ContextTab telemetry={contextTelemetry} contextWindowTokens={contextWindowTokens} />
      </TabsContent>
    </Tabs>
  );
};

// ── Full Raw View with search ──────────────────────────────────────────

const FullRawView: React.FC<{ content: string }> = ({ content }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatch, setCurrentMatch] = useState(0);
  const [matchCount, setMatchCount] = useState(0);
  const preRef = useRef<HTMLPreElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Open search on Cmd/Ctrl+F
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        // Only intercept if this component's container is visible
        if (preRef.current) {
          e.preventDefault();
          setSearchOpen(true);
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }
      }
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [searchOpen]);

  // Highlight matches and scroll to current
  const highlightAndScroll = useCallback(() => {
    const el = preRef.current;
    if (!el) return;

    // Remove existing highlights
    const existing = el.querySelectorAll('mark');
    existing.forEach(mark => {
      const parent = mark.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
        parent.normalize();
      }
    });

    if (!searchQuery) {
      setMatchCount(0);
      setCurrentMatch(0);
      return;
    }

    // Walk text nodes and highlight
    const treeWalker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const matches: { node: Text; index: number }[] = [];
    const lowerQuery = searchQuery.toLowerCase();

    let node: Text | null;
    while ((node = treeWalker.nextNode() as Text | null)) {
      const text = node.textContent || '';
      const lowerText = text.toLowerCase();
      let idx = 0;
      while ((idx = lowerText.indexOf(lowerQuery, idx)) !== -1) {
        matches.push({ node, index: idx });
        idx += lowerQuery.length;
      }
    }

    setMatchCount(matches.length);
    if (matches.length === 0) {
      setCurrentMatch(0);
      return;
    }

    // Apply highlights in reverse order to preserve indices
    const nodeGroups = new Map<Text, { index: number; matchIdx: number }[]>();
    matches.forEach((m, matchIdx) => {
      if (!nodeGroups.has(m.node)) nodeGroups.set(m.node, []);
      nodeGroups.get(m.node)!.push({ index: m.index, matchIdx });
    });

    const safeCurrentMatch = Math.min(currentMatch, matches.length - 1);

    for (const [textNode, positions] of nodeGroups) {
      // Sort positions in reverse to process from end to start
      const sorted = [...positions].sort((a, b) => b.index - a.index);
      const baseNode: Text = textNode;
      for (const pos of sorted) {
        const text = baseNode.textContent || '';
        if (pos.index + lowerQuery.length > text.length) continue;

        const before = text.slice(0, pos.index);
        const match = text.slice(pos.index, pos.index + lowerQuery.length);
        const after = text.slice(pos.index + lowerQuery.length);

        const mark = document.createElement('mark');
        mark.textContent = match;
        mark.className = pos.matchIdx === safeCurrentMatch
          ? 'bg-warning text-warning-foreground rounded-sm px-px'
          : 'bg-warning/40 rounded-sm px-px';

        const parent = baseNode.parentNode!;
        if (after) parent.insertBefore(document.createTextNode(after), baseNode.nextSibling);
        parent.insertBefore(mark, baseNode.nextSibling);
        baseNode.textContent = before;

        // Scroll to current match
        if (pos.matchIdx === safeCurrentMatch) {
          mark.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }
    }
  }, [searchQuery, currentMatch]);

  useEffect(() => {
    highlightAndScroll();
  }, [highlightAndScroll]);

  const goToMatch = (direction: 'next' | 'prev') => {
    if (matchCount === 0) return;
    setCurrentMatch(prev => {
      if (direction === 'next') return (prev + 1) % matchCount;
      return (prev - 1 + matchCount) % matchCount;
    });
  };

  return (
    <div className="relative">
      {searchOpen && (
        <div className="sticky top-0 z-10 flex items-center gap-1.5 bg-card border rounded-md p-1.5 mb-2 shadow-sm">
          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <input
            ref={searchInputRef}
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setCurrentMatch(0); }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                goToMatch(e.shiftKey ? 'prev' : 'next');
              }
              if (e.key === 'Escape') {
                setSearchOpen(false);
                setSearchQuery('');
              }
            }}
            placeholder="Search..."
            className="flex-1 text-xs bg-transparent outline-none placeholder:text-muted-foreground/50"
            autoFocus
          />
          {searchQuery && (
            <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
              {matchCount > 0 ? `${currentMatch + 1}/${matchCount}` : '0/0'}
            </span>
          )}
          <button onClick={() => goToMatch('prev')} className="p-0.5 hover:bg-muted rounded" disabled={matchCount === 0}>
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => goToMatch('next')} className="p-0.5 hover:bg-muted rounded" disabled={matchCount === 0}>
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => { setSearchOpen(false); setSearchQuery(''); }} className="p-0.5 hover:bg-muted rounded">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <pre
        ref={preRef}
        className="text-xs leading-relaxed whitespace-pre-wrap bg-muted/50 rounded-md p-3 border font-mono max-h-[70vh] overflow-auto"
      >
        {content}
      </pre>
    </div>
  );
};

// ── Tab 1: Prompts ─────────────────────────────────────────────────────

const PromptsTab: React.FC<{ sections: SystemPromptSection[] }> = ({ sections }) => {
  const [viewMode, setViewMode] = useState<'sections' | 'full-raw'>('sections');
  const grouped = groupSectionsByCategory(sections);
  const totalTokens = sections.reduce((sum, s) => sum + (s.estimated_tokens || 0), 0);

  const fullRawContent = sections
    .map(s => `### ${s.label} ###\n${s.content}`)
    .join('\n\n');

  return (
    <div>
      <div className="flex items-center justify-between mb-3 pb-2 border-b text-xs text-muted-foreground">
        {totalTokens > 0 ? (
          <span>Total prompt tokens: <TokenBadge tokens={totalTokens} className="text-xs" /></span>
        ) : <span />}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setViewMode('sections')}
            className={cn(
              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] transition-colors',
              viewMode === 'sections' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Eye className="h-3 w-3" />
            Sections
          </button>
          <button
            onClick={() => setViewMode('full-raw')}
            className={cn(
              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] transition-colors',
              viewMode === 'full-raw' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Code className="h-3 w-3" />
            Full Raw
          </button>
        </div>
      </div>

      {viewMode === 'full-raw' ? (
        <FullRawView content={fullRawContent} />
      ) : (
        <div className="space-y-4">
          {CATEGORY_ORDER.map(cat => {
            const catSections = grouped[cat];
            if (!catSections?.length) return null;
            const catTokens = catSections.reduce((sum, s) => sum + (s.estimated_tokens || 0), 0);
            return (
              <div key={cat}>
                <div className="flex items-center justify-between mb-1.5">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">
                    {CATEGORY_LABELS[cat] || cat}
                  </h4>
                  {catTokens > 0 && <TokenBadge tokens={catTokens} />}
                </div>
                <div className="space-y-1.5">
                  {catSections.map(section => (
                    <PromptSection key={section.key} section={section} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Tab 2: Tools ───────────────────────────────────────────────────────

const ToolsTab: React.FC<{
  tools: DebugToolDetail[];
  totals?: SystemPromptDebugInfo['totals'] | null;
}> = ({ tools, totals }) => {
  const grouped = groupToolsByModule(tools);
  const moduleNames = Object.keys(grouped).sort();

  return (
    <div>
      {totals && (
        <div className="flex items-center justify-between mb-3 pb-2 border-b text-xs text-muted-foreground">
          <span>
            {totals.tool_count} tools across {moduleNames.length} modules
          </span>
          <TokenBadge tokens={totals.tool_estimated_tokens} className="text-xs" />
        </div>
      )}
      <div className="space-y-3">
        {moduleNames.map(moduleName => {
          const moduleTools = grouped[moduleName];
          const moduleTokens = moduleTools.reduce((sum, t) => sum + t.estimated_tokens, 0);
          return (
            <ToolModuleGroup
              key={moduleName}
              moduleName={moduleName}
              tools={moduleTools}
              estimatedTokens={moduleTokens}
            />
          );
        })}
      </div>
      {tools.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">No MCP tools configured for this session.</p>
      )}
    </div>
  );
};

const ToolModuleGroup: React.FC<{
  moduleName: string;
  tools: DebugToolDetail[];
  estimatedTokens: number;
}> = ({ moduleName, tools, estimatedTokens }) => {
  const [open, setOpen] = useState(false);
  const displayName = moduleName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full py-1.5 text-sm hover:text-foreground text-muted-foreground transition-colors">
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <span className="font-medium">{displayName}</span>
          <span className="text-[10px] text-muted-foreground/60">({tools.length} tools)</span>
        </div>
        <TokenBadge tokens={estimatedTokens} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-5 space-y-1 mt-1">
          {tools.map(tool => (
            <ToolRow key={tool.name} tool={tool} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const ToolRow: React.FC<{ tool: DebugToolDetail }> = ({ tool }) => {
  const [expanded, setExpanded] = useState(false);
  const shortName = tool.name.includes('__') ? tool.name.split('__').slice(1).join('__') : tool.name;

  return (
    <div className="border-l-2 border-muted pl-2.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left py-0.5 text-xs hover:text-foreground text-muted-foreground transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono font-medium truncate">{shortName}</span>
          <span className="text-[10px] text-muted-foreground/50 shrink-0">
            {tool.param_count}p
            {tool.required_params.length > 0 && ` (${tool.required_params.length} req)`}
          </span>
        </div>
        <TokenBadge tokens={tool.estimated_tokens} />
      </button>
      {expanded && (
        <div className="mt-1 mb-2">
          <pre className="text-[11px] leading-relaxed whitespace-pre-wrap bg-muted/50 rounded-md p-2 border font-mono max-h-40 overflow-auto">
            {tool.description}
          </pre>
          {tool.required_params.length > 0 && (
            <div className="mt-1 text-[10px] text-muted-foreground/70">
              <span className="font-medium">Required:</span> {tool.required_params.join(', ')}
            </div>
          )}
          <div className="mt-0.5 text-[10px] text-muted-foreground/50">
            Schema: {tool.schema_chars.toLocaleString()} chars
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tab 3: Summary ─────────────────────────────────────────────────────

// ── Context telemetry tab ─────────────────────────────────────────────

const ContextTab: React.FC<{
  telemetry: ContextTelemetryRound[] | null;
  contextWindowTokens: number | null;
}> = ({ telemetry, contextWindowTokens }) => {
  if (!telemetry || telemetry.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No context telemetry recorded yet. Telemetry is captured after agentic loop runs (tool-using turns).</p>;
  }

  const peak = Math.max(...telemetry.map(t => t.est_context_tokens));
  const final = telemetry[telemetry.length - 1].est_context_tokens;
  const windowTokens = contextWindowTokens || 200000;
  const peakPressure = Math.round((peak / windowTokens) * 100);
  const finalPressure = Math.round((final / windowTokens) * 100);

  const pressureColor = (pct: number) =>
    pct >= 80 ? 'bg-destructive' : pct >= 50 ? 'bg-warning' : 'bg-info/60';

  return (
    <div className="space-y-4">
      {/* Pressure gauge */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">Context Pressure</h4>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground flex-1">Peak</span>
            <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
              <div className={cn('h-full rounded-full', pressureColor(peakPressure))} style={{ width: `${Math.min(peakPressure, 100)}%` }} />
            </div>
            <span className="font-mono tabular-nums w-20 text-right">~{peak.toLocaleString()}</span>
            <span className="font-mono text-muted-foreground/50 w-10 text-right">{peakPressure}%</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground flex-1">Final</span>
            <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
              <div className={cn('h-full rounded-full', pressureColor(finalPressure))} style={{ width: `${Math.min(finalPressure, 100)}%` }} />
            </div>
            <span className="font-mono tabular-nums w-20 text-right">~{final.toLocaleString()}</span>
            <span className="font-mono text-muted-foreground/50 w-10 text-right">{finalPressure}%</span>
          </div>
          <div className="flex items-center gap-2 text-xs pt-1">
            <span className="text-muted-foreground flex-1">Model window</span>
            <span className="font-mono tabular-nums">~{windowTokens.toLocaleString()} tokens</span>
          </div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">Summary</h4>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <span className="text-muted-foreground">Rounds tracked</span>
          <span className="font-mono">{telemetry.length}</span>
          <span className="text-muted-foreground">Peak tokens</span>
          <span className="font-mono">~{peak.toLocaleString()}</span>
          <span className="text-muted-foreground">Final tokens</span>
          <span className="font-mono">~{final.toLocaleString()}</span>
          {telemetry.length > 1 && (
            <>
              <span className="text-muted-foreground">Growth (last turn)</span>
              <span className="font-mono">
                {final > telemetry[telemetry.length - 2].est_context_tokens ? '+' : ''}
                {(final - telemetry[telemetry.length - 2].est_context_tokens).toLocaleString()}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Per-round detail */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">Per-Round Detail</h4>
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/30 border-b">
                <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">Round</th>
                <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Messages</th>
                <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Context</th>
                <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">System</th>
                <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Est. Tokens</th>
                <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Pressure</th>
              </tr>
            </thead>
            <tbody>
              {telemetry.map((round, i) => {
                const pressure = Math.round((round.est_context_tokens / windowTokens) * 100);
                const prevTokens = i > 0 ? telemetry[i - 1].est_context_tokens : 0;
                const delta = round.est_context_tokens - prevTokens;
                return (
                  <tr key={round.round} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="px-2 py-1 font-mono">{round.round}</td>
                    <td className="px-2 py-1 text-right font-mono">{round.message_count}</td>
                    <td className="px-2 py-1 text-right font-mono">{round.context_chars.toLocaleString()}</td>
                    <td className="px-2 py-1 text-right font-mono">{round.system_chars.toLocaleString()}</td>
                    <td className="px-2 py-1 text-right font-mono">
                      ~{round.est_context_tokens.toLocaleString()}
                      {i > 0 && (
                        <span className={cn('ml-1', delta > 0 ? 'text-warning' : 'text-success')}>
                          ({delta > 0 ? '+' : ''}{delta.toLocaleString()})
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right">
                      <span className={cn(
                        'font-mono',
                        pressure >= 80 ? 'text-destructive font-medium' : pressure >= 50 ? 'text-warning' : 'text-muted-foreground',
                      )}>
                        {pressure}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Summary tab ───────────────────────────────────────────────────────

const SummaryTab: React.FC<{
  debugInfo: SystemPromptDebugInfo | null;
  sections: SystemPromptSection[];
}> = ({ debugInfo, sections }) => {
  if (!debugInfo) return <p className="text-sm text-muted-foreground py-4">No debug data available.</p>;

  const { totals } = debugInfo;
  const grouped = groupSectionsByCategory(sections);

  const categoryTotals = CATEGORY_ORDER
    .filter(cat => cat !== 'tools')
    .map(cat => {
      const catSections = grouped[cat] || [];
      const tokens = catSections.reduce((sum, s) => sum + (s.estimated_tokens || 0), 0);
      return { category: cat, tokens };
    })
    .filter(c => c.tokens > 0);

  return (
    <div>
      <div className="space-y-4">
        {/* Model & modules */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">Session</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <span className="text-muted-foreground">Model</span>
            <span className="font-mono">{debugInfo.model_category || 'default'}</span>
            <span className="text-muted-foreground">Modules</span>
            <span className="font-mono">{debugInfo.enabled_modules.length > 0 ? debugInfo.enabled_modules.join(', ') : 'none'}</span>
          </div>
        </div>

        {/* Token breakdown */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">Token Budget (estimated)</h4>
          <div className="space-y-1.5">
            {categoryTotals.map(({ category, tokens }) => (
              <TokenRow key={category} label={`${CATEGORY_LABELS[category]} prompts`} tokens={tokens} total={totals.total_estimated_tokens} />
            ))}
            <TokenRow label={`Tool definitions (${totals.tool_count} tools)`} tokens={totals.tool_estimated_tokens} total={totals.total_estimated_tokens} />
            <div className="border-t pt-1.5 mt-1.5">
              <TokenRow label="Total context" tokens={totals.total_estimated_tokens} total={totals.total_estimated_tokens} bold />
            </div>
          </div>
        </div>

        {/* Raw sizes */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">Raw Sizes</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <span className="text-muted-foreground">Prompt text</span>
            <span className="font-mono">{totals.prompt_chars.toLocaleString()} chars</span>
            <span className="text-muted-foreground">Tool schemas</span>
            <span className="font-mono">{totals.tool_schema_chars.toLocaleString()} chars</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const TokenRow: React.FC<{
  label: string;
  tokens: number;
  total: number;
  bold?: boolean;
}> = ({ label, tokens, total, bold }) => {
  const pct = total > 0 ? Math.round((tokens / total) * 100) : 0;

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={cn('flex-1', bold ? 'font-medium' : 'text-muted-foreground')}>{label}</span>
      <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-info/60 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className={cn('font-mono tabular-nums w-20 text-right', bold && 'font-medium')}>
        ~{tokens.toLocaleString()}
      </span>
      <span className="font-mono text-muted-foreground/50 w-10 text-right">{pct}%</span>
    </div>
  );
};

// ── Shared: Prompt section (collapsible) ───────────────────────────────

/** Content viewer popup for viewing full document content */
const ContentPopup: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  content: string;
}> = ({ open, onClose, title, content }) => {
  const [viewRaw, setViewRaw] = useState(false);

  return (
    <ResponsiveDialog open={open} onOpenChange={(v) => !v && onClose()} title={title} className="max-w-2xl">
      <div className="flex items-center justify-end gap-1 mb-1">
        <button
          onClick={() => setViewRaw(false)}
          className={cn(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] transition-colors',
            !viewRaw ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Eye className="h-3 w-3" />
          Rendered
        </button>
        <button
          onClick={() => setViewRaw(true)}
          className={cn(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] transition-colors',
            viewRaw ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Code className="h-3 w-3" />
          Raw
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        {viewRaw ? (
          <pre className="text-xs leading-relaxed whitespace-pre-wrap bg-muted/50 rounded-md p-3 border font-mono">
            {content}
          </pre>
        ) : (
          <div className="rounded-md border bg-muted/50 p-3">
            <MarkdownDisplay content={content} variant="compact" />
          </div>
        )}
      </div>
    </ResponsiveDialog>
  );
};

/** Loading mode badge colors */
const LOADING_MODE_STYLES: Record<string, string> = {
  full: 'bg-info/10 text-info',
  summary: 'bg-warning/10 text-warning',
  reference: 'bg-muted text-muted-foreground',
};

/** Icon for child items based on source type */
const ChildIcon: React.FC<{ source?: string }> = ({ source }) => {
  if (source === 'entity') return <Kanban className="h-3 w-3 text-muted-foreground/60 shrink-0" />;
  return <FileText className="h-3 w-3 text-muted-foreground/60 shrink-0" />;
};

/** Format source label for display */
const formatSource = (source: string): string =>
  source.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** Single child item in a nested section (e.g., one briefing document) */
const SectionChildItem: React.FC<{ child: SystemPromptSectionChild }> = ({ child }) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const hasPopup = !!child.popup_content;

  return (
    <>
      <div className="flex items-center justify-between py-1.5 pl-5 text-xs group">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <ChildIcon source={child.source} />
          <span className="font-medium truncate">{child.label}</span>
          {child.description && (
            <span className="text-muted-foreground/60 truncate hidden sm:inline text-[10px]">{child.description}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {child.loading_mode && (
            <Badge variant="outline" className={cn('text-[9px] px-1 py-0 h-3.5 shrink-0', LOADING_MODE_STYLES[child.loading_mode])}>
              {child.loading_mode}
            </Badge>
          )}
          {child.source && child.source !== 'inline' && child.source !== 'unknown' && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 shrink-0 text-muted-foreground/60">
              {formatSource(child.source)}
            </Badge>
          )}
          {child.estimated_tokens ? <TokenBadge tokens={child.estimated_tokens} /> : null}
          {hasPopup && (
            <button
              onClick={() => setPopupOpen(true)}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover:opacity-100"
            >
              <Eye className="h-3 w-3" />
              View
            </button>
          )}
        </div>
      </div>
      {/* Metadata row: external ID, access level */}
      {(child.external_id || child.access_level) && (
        <div className="flex items-center gap-2 pl-10 pb-0.5 text-[10px] text-muted-foreground/50">
          {child.external_id && (
            <span className="font-mono truncate max-w-[200px]">ID: {child.external_id}</span>
          )}
          {child.access_level && child.access_level !== 'read' && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 bg-success/10 text-success">
              {child.access_level}
            </Badge>
          )}
        </div>
      )}
      {hasPopup && (
        <ContentPopup
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          title={child.label}
          content={child.popup_content!}
        />
      )}
    </>
  );
};

const PromptSection: React.FC<{ section: SystemPromptSection }> = ({ section }) => {
  const [open, setOpen] = useState(false);
  const [viewRaw, setViewRaw] = useState(false);
  const hasChildren = section.children && section.children.length > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full py-1 text-sm hover:text-foreground text-muted-foreground transition-colors">
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <span className="font-medium">{section.label}</span>
          {hasChildren && (
            <span className="text-[10px] text-muted-foreground/50">({section.children!.length})</span>
          )}
        </div>
        {section.estimated_tokens ? <TokenBadge tokens={section.estimated_tokens} /> : null}
      </CollapsibleTrigger>
      <CollapsibleContent>
        {hasChildren ? (
          /* Nested view: show individual children with view buttons */
          <div className="border-l-2 border-muted ml-1.5 mt-0.5">
            {section.children!.map(child => (
              <SectionChildItem key={child.key} child={child} />
            ))}
          </div>
        ) : (
          /* Flat view: show rendered/raw content */
          <>
            <div className="flex items-center justify-end gap-1 mb-1">
              <button
                onClick={(e) => { e.stopPropagation(); setViewRaw(false); }}
                className={cn(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] transition-colors',
                  !viewRaw ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Eye className="h-3 w-3" />
                Rendered
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setViewRaw(true); }}
                className={cn(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] transition-colors',
                  viewRaw ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Code className="h-3 w-3" />
                Raw
              </button>
            </div>
            {viewRaw ? (
              <pre className="text-xs leading-relaxed whitespace-pre-wrap bg-muted/50 rounded-md p-3 border font-mono max-h-60 overflow-auto">
                {section.content}
              </pre>
            ) : (
              <div className="max-h-60 overflow-auto rounded-md border bg-muted/50 p-3">
                <MarkdownDisplay content={section.content} variant="compact" />
              </div>
            )}
          </>
        )}
        {section.char_count ? (
          <div className="text-[10px] text-muted-foreground/50 mt-0.5 text-right">
            {section.char_count.toLocaleString()} chars
          </div>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
};
